'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import Icon from '@/components/ui/AppIcon';
import type { MarketStats } from '@/lib/hyperliquid/types';
import { fetchMetaAndAssetCtxs, fetchAllMids } from '@/lib/hyperliquid/service';

export interface TradingPair {
  symbol: string;
  base: string;
  quote: string;
  price: string;
  change: string;
  positive: boolean;
  volume: string;
  high: string;
  low: string;
  leverage?: string;
  fundingRate?: string;
  openInterest?: string;
  category?: string;
}

// Map of token symbols to their cryptocurrency-icons CDN paths
const CRYPTO_LOGO_BASE = 'https://cdn.jsdelivr.net/npm/cryptocurrency-icons@0.18.1/svg/color/';
const KNOWN_ICONS = new Set([
  'btc','eth','sol','bnb','xrp','ada','doge','avax','matic','link','uni','ltc','dot','atom',
  'near','arb','op','inj','apt','sui','tia','sei','hype','pepe','wif','bonk','jup','strk',
  'manta','trx','shib','ton','fil','icp','stx','rndr','fet','grt','ldo','crv','snx','mkr',
  'aave','comp','sushi','1inch','yfi','bal','uma','zrx','band','knc','ens','imx','blur',
  'magic','gmx','rdnt','pendle','wld','pyth','jto','w','zeta','alt','mew','bome','popcat',
  'neiro','goat','pnut','act','moodeng','chillguy','fartcoin','ai16z','zerebro','griffain',
  'virtual','aixbt','arc','vader','swarms','iq','cookie','anon','luce','turbo','gme','trump',
  'melania','vine','broccoli','paxg','xaut','move','me','usual','morpho','eigen','safe',
  'zk','taiko','scroll','manta','blast','mode','linea','base','zora','degen','higher',
  'brett','toshi','aerodrome','aero','cbbtc','wbtc','weeth','rseth','ezeth','reth','steth',
  'osmo','kava','evmos','canto','juno','stars','huahua','scrt','akash','akt','rune','thor',
  'luna','lunc','ustc','mina','cfx','kas','hbar','algo','xlm','vet','iota','xtz','eos',
  'bch','bsv','zec','dash','dcr','xmr','etc','neo','ont','waves','zen','sc','dgb','sys',
  'vtc','rdd','nxt','burst','xem','lsk','ark','steem','hive','blurt','piv','nav','part',
]);

function getLogoUrl(symbol: string): string | null {
  const lower = symbol.toLowerCase();
  if (KNOWN_ICONS.has(lower)) {
    return `${CRYPTO_LOGO_BASE}${lower}.svg`;
  }
  return null;
}

function CryptoLogo({ symbol, size = 20 }: { symbol: string; size?: number }) {
  const [imgError, setImgError] = useState(false);
  const logoUrl = getLogoUrl(symbol);

  if (!logoUrl || imgError) {
    return (
      <div
        className="flex items-center justify-center bg-[#4c6ef4] shrink-0"
        style={{ width: size, height: size }}
      >
        <span style={{ fontSize: size * 0.38 }} className="font-bold text-white leading-none">
          {symbol.slice(0, 2)}
        </span>
      </div>
    );
  }

  return (
    <img
      src={logoUrl}
      alt={`${symbol} logo`}
      width={size}
      height={size}
      className="shrink-0"
      style={{ width: size, height: size }}
      onError={() => setImgError(true)}
    />
  );
}

function formatPrice(price: number): string {
  if (price >= 10000) return price.toLocaleString('en-US', { maximumFractionDigits: 1 });
  if (price >= 100) return price.toFixed(2);
  if (price >= 1) return price.toFixed(4);
  if (price >= 0.001) return price.toFixed(6);
  return price.toFixed(8);
}

function formatVolume(vol: number): string {
  const v = Number(vol);
  if (!isFinite(v)) return '—';
  if (v >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
  return `$${v.toFixed(0)}`;
}

function formatOI(oi: number, price: number): string {
  const usd = Number(oi) * Number(price);
  if (!isFinite(usd)) return '—';
  if (usd >= 1e9) return `$${(usd / 1e9).toFixed(2)}B`;
  if (usd >= 1e6) return `$${(usd / 1e6).toFixed(1)}M`;
  if (usd >= 1e3) return `$${(usd / 1e3).toFixed(1)}K`;
  return `$${usd.toFixed(0)}`;
}

function formatFunding(rate: number): string {
  const r = Number(rate);
  if (!isFinite(r)) return '0.0000%';
  const pct = (r * 100).toFixed(4);
  return `${r >= 0 ? '' : ''}${pct}%`;
}

// Skeleton row for loading state
function SkeletonRow() {
  return (
    <div className="grid px-3 py-2.5 items-center animate-pulse" style={{ gridTemplateColumns: '20px 1fr 90px 80px 80px 90px 100px' }}>
      <div className="w-3 h-3 bg-[#2a2a2a] rounded-sm" />
      <div className="flex items-center gap-2">
        <div className="w-5 h-5 bg-[#2a2a2a] rounded-sm shrink-0" />
        <div className="flex flex-col gap-1">
          <div className="w-16 h-3 bg-[#2a2a2a] rounded-sm" />
          <div className="w-8 h-2 bg-[#2a2a2a] rounded-sm" />
        </div>
      </div>
      <div className="ml-auto w-16 h-3 bg-[#2a2a2a] rounded-sm" />
      <div className="ml-auto w-12 h-3 bg-[#2a2a2a] rounded-sm" />
      <div className="ml-auto w-14 h-3 bg-[#2a2a2a] rounded-sm" />
      <div className="ml-auto w-12 h-3 bg-[#2a2a2a] rounded-sm" />
      <div className="ml-auto w-16 h-3 bg-[#2a2a2a] rounded-sm" />
    </div>
  );
}

const MARKET_CATEGORIES = ['All markets', 'Top', 'Meme', 'AI', 'New'];

// Fallback ETH pair used before data loads
const ETH_FALLBACK: TradingPair = {
  symbol: 'ETH/USDC',
  base: 'ETH',
  quote: 'USDC',
  price: '—',
  change: '—',
  positive: true,
  volume: '—',
  high: '—',
  low: '—',
  leverage: '200x',
  fundingRate: '0.0000%',
  openInterest: '—',
  category: 'Top',
};

// Assign category heuristically
function assignCategory(symbol: string): string {
  const memes = ['DOGE','SHIB','PEPE','WIF','BONK','BOME','POPCAT','NEIRO','GOAT','PNUT','ACT','MOODENG','CHILLGUY','FARTCOIN','GME','TRUMP','MELANIA','VINE','BROCCOLI','MEW','TURBO'];
  const ai = ['FET','RNDR','GRT','INJ','TIA','WLD','PYTH','AI16Z','ZEREBRO','GRIFFAIN','VIRTUAL','AIXBT','ARC','VADER','SWARMS','IQ','COOKIE','ANON'];
  const top = ['BTC','ETH','SOL','BNB','XRP','ADA','AVAX','MATIC','LINK','DOT','ATOM','LTC','UNI','NEAR','ARB','OP','APT','SUI','SEI','HYPE'];
  if (memes.includes(symbol)) return 'Meme';
  if (ai.includes(symbol)) return 'AI';
  if (top.includes(symbol)) return 'Top';
  return 'New';
}

interface TradingPairSelectorProps {
  selectedPair: TradingPair;
  onSelectPair: (pair: TradingPair) => void;
  liveStats?: MarketStats | null;
}

export default function TradingPairSelector({ selectedPair, onSelectPair, liveStats }: TradingPairSelectorProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'Favorites' | 'Futures' | 'Spot'>('Futures');
  const [activeCategory, setActiveCategory] = useState('All markets');
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Dynamic pair list state
  const [pairs, setPairs] = useState<TradingPair[]>([]);
  const [loading, setLoading] = useState(true);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Store meta info (leverage, funding, OI, prevDayPx) keyed by symbol
  const metaRef = useRef<Record<string, { maxLeverage: number; funding: number; openInterest: number; prevDayPx: number; dayNtlVlm: number }>>({});

  const buildPairs = useCallback((mids: Record<string, string>): TradingPair[] => {
    const meta = metaRef.current;
    return Object.entries(meta).map(([symbol, info]) => {
      const midStr = mids[symbol];
      const price = midStr ? parseFloat(midStr) : 0;
      const prevDay = info.prevDayPx;
      const changePct = prevDay > 0 ? ((price - prevDay) / prevDay) * 100 : 0;
      const changeStr = `${changePct >= 0 ? '+' : ''}${changePct.toFixed(2)}%`;
      return {
        symbol: `${symbol}/USDC`,
        base: symbol,
        quote: 'USDC',
        price: price > 0 ? formatPrice(price) : '—',
        change: changeStr,
        positive: changePct >= 0,
        volume: formatVolume(info.dayNtlVlm),
        high: '—',
        low: '—',
        leverage: `${info.maxLeverage}x`,
        fundingRate: formatFunding(info.funding),
        openInterest: price > 0 ? formatOI(info.openInterest, price) : '—',
        category: assignCategory(symbol),
      };
    }).sort((a, b) => {
      // Sort by OI descending (approximate by raw OI * price)
      const aOI = metaRef.current[a.base]?.openInterest ?? 0;
      const bOI = metaRef.current[b.base]?.openInterest ?? 0;
      const aPrice = parseFloat(mids[a.base] ?? '0');
      const bPrice = parseFloat(mids[b.base] ?? '0');
      return (bOI * bPrice) - (aOI * aPrice);
    });
  }, []);

  const loadInitial = useCallback(async () => {
    try {
      const [metaRaw, ctxs] = await fetchMetaAndAssetCtxs();
      const meta = metaRaw as { universe: { name: string; maxLeverage: number }[] };
      const ctxArr = ctxs as { dayNtlVlm: number; prevDayPx: number; markPx: number; funding: number; openInterest: number }[];

      // Build meta map
      const newMeta: typeof metaRef.current = {};
      meta.universe.forEach((asset, i) => {
        const ctx = ctxArr[i];
        if (!ctx) return;
        newMeta[asset.name] = {
          maxLeverage: Number(asset.maxLeverage),
          funding: Number(ctx.funding),
          openInterest: Number(ctx.openInterest),
          prevDayPx: Number(ctx.prevDayPx),
          dayNtlVlm: Number(ctx.dayNtlVlm),
        };
      });
      metaRef.current = newMeta;

      // Fetch live mids
      const mids = await fetchAllMids();
      const builtPairs = buildPairs(mids);
      setPairs(builtPairs);

      // Default selection: keep current if valid, else ETH, else first
      const currentBase = selectedPair.base;
      const found = builtPairs.find(p => p.base === currentBase);
      if (!found || selectedPair.price === '—') {
        const ethPair = builtPairs.find(p => p.base === 'ETH');
        if (ethPair) onSelectPair(ethPair);
        else if (builtPairs.length > 0) onSelectPair(builtPairs[0]);
      } else {
        // Update selected pair with fresh data
        onSelectPair(found);
      }
    } catch (err) {
      console.error('TradingPairSelector: initial load failed', err);
    } finally {
      setLoading(false);
    }
  }, [buildPairs, selectedPair.base, selectedPair.price, onSelectPair]);

  const pollMids = useCallback(async () => {
    if (Object.keys(metaRef.current).length === 0) return;
    try {
      const mids = await fetchAllMids();
      const builtPairs = buildPairs(mids);
      setPairs(builtPairs);
      // Update selected pair price live
      const updated = builtPairs.find(p => p.base === selectedPair.base);
      if (updated) onSelectPair(updated);
    } catch {
      // silently ignore poll errors
    }
  }, [buildPairs, selectedPair.base, onSelectPair]);

  useEffect(() => {
    loadInitial();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (loading) return;
    pollTimerRef.current = setInterval(pollMids, 3000);
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, [loading, pollMids]);

  const displayPrice = liveStats?.markPrice ?? selectedPair.price;
  const displayChange = liveStats?.change24h ?? selectedPair.change;
  const displayPositive = liveStats?.positive ?? selectedPair.positive;
  const displayVolume = liveStats?.dayVolume ?? selectedPair.volume;

  useEffect(() => {
    if (open && searchRef.current) {
      setTimeout(() => searchRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const toggleFavorite = (symbol: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setFavorites(prev => {
      const next = new Set(prev);
      if (next.has(symbol)) next.delete(symbol);
      else next.add(symbol);
      return next;
    });
  };

  const filteredPairs = pairs.filter(pair => {
    const matchesSearch = pair.symbol.toLowerCase().includes(searchQuery.toLowerCase()) ||
      pair.base.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = activeCategory === 'All markets' || pair.category === activeCategory;
    const matchesFavorites = activeTab === 'Favorites' ? favorites.has(pair.symbol) : true;
    return matchesSearch && matchesCategory && matchesFavorites;
  });

  return (
    <div className="flex items-center gap-4 flex-wrap">
      {/* Pair Selector Button */}
      <div className="relative" ref={panelRef}>
        <button
          onClick={() => setOpen(!open)}
          className="flex items-center gap-2 px-4 py-2 bg-[#242424] border border-[#2a2a2a] hover:bg-[#2a2a2a] transition-all duration-200"
        >
          <CryptoLogo symbol={selectedPair.base} size={22} />
          <div className="flex flex-col items-start">
            <span className="text-base font-bold text-[#F1F5F9] leading-tight">{selectedPair.symbol}</span>
            <span className="text-[10px] text-[#f97316] font-medium leading-tight">Perp</span>
          </div>
          <Icon name="ChevronDownIcon" size={16} className={`text-[#94A3B8] transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
        </button>

        {/* Full Side Panel */}
        {open && (
          <div
            className="absolute top-full left-0 mt-1 bg-[#141414] border border-[#2a2a2a] z-[200] shadow-2xl flex flex-col"
            style={{ width: '680px', maxHeight: '520px' }}
          >
            {/* Search Input */}
            <div className="p-3 border-b border-[#2a2a2a]">
              <div className="flex items-center gap-2 bg-[#1e1e1e] border border-[#2a2a2a] px-3 py-2">
                <Icon name="MagnifyingGlassIcon" size={16} className="text-[#94A3B8] shrink-0" />
                <input
                  ref={searchRef}
                  type="text"
                  placeholder="Search"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="flex-1 bg-transparent text-sm text-[#F1F5F9] placeholder-[#4a4a4a] outline-none"
                />
                {loading && (
                  <span className="text-[10px] text-[#94A3B8] animate-pulse">Loading…</span>
                )}
              </div>
            </div>

            {/* Tabs: Favorites / Futures / Spot */}
            <div className="flex items-center gap-0 px-3 pt-2 pb-0 border-b border-[#2a2a2a]">
              {(['Favorites', 'Futures', 'Spot'] as const).map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-all duration-150 border-b-2 -mb-px ${
                    activeTab === tab
                      ? 'text-[#4c6ef4] border-[#4c6ef4] bg-[rgba(76,110,244,0.08)]'
                      : 'text-[#94A3B8] border-transparent hover:text-[#F1F5F9]'
                  }`}
                >
                  {tab === 'Favorites' && (
                    <Icon name="StarIcon" size={13} className={activeTab === 'Favorites' ? 'text-[#4c6ef4]' : 'text-[#94A3B8]'} />
                  )}
                  {tab}
                </button>
              ))}
            </div>

            {/* Market Category Filter Row */}
            <div className="flex items-center gap-1 px-3 py-2 border-b border-[#2a2a2a] flex-wrap">
              {MARKET_CATEGORIES.map(cat => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-2.5 py-1 text-xs font-medium transition-all duration-150 ${
                    activeCategory === cat
                      ? 'bg-[rgba(76,110,244,0.15)] text-[#4c6ef4] border border-[rgba(76,110,244,0.4)]'
                      : 'text-[#94A3B8] hover:text-[#F1F5F9] hover:bg-[#1e1e1e] border border-transparent'
                  }`}
                >
                  {cat}
                </button>
              ))}
              {!loading && pairs.length > 0 && (
                <span className="ml-auto text-[10px] text-[#4a4a4a]">{pairs.length} markets</span>
              )}
            </div>

            {/* Column Headers */}
            <div className="grid px-3 py-2 border-b border-[#2a2a2a] text-[11px] text-[#94A3B8] font-medium" style={{ gridTemplateColumns: '20px 1fr 90px 80px 80px 90px 100px' }}>
              <div />
              <div>Symbols</div>
              <div className="text-right">Last price</div>
              <div className="text-right">24h change</div>
              <div className="text-right">Funding Rate</div>
              <div className="text-right">Volume</div>
              <div className="text-right">Open interest</div>
            </div>

            {/* Scrollable Pair List */}
            <div className="overflow-y-auto flex-1" style={{ maxHeight: '320px' }}>
              {loading ? (
                // Loading skeleton
                Array.from({ length: 10 }).map((_, i) => <SkeletonRow key={i} />)
              ) : filteredPairs.length === 0 ? (
                <div className="flex items-center justify-center py-12 text-[#94A3B8] text-sm">
                  No pairs found
                </div>
              ) : (
                filteredPairs.map(pair => (
                  <button
                    key={pair.symbol}
                    onClick={() => { onSelectPair(pair); setOpen(false); setSearchQuery(''); }}
                    className={`w-full grid px-3 py-2.5 transition-all duration-100 hover:bg-[#1e1e1e] text-left items-center ${
                      selectedPair.symbol === pair.symbol ? 'bg-[rgba(76,110,244,0.08)]' : ''
                    }`}
                    style={{ gridTemplateColumns: '20px 1fr 90px 80px 80px 90px 100px' }}
                  >
                    {/* Star */}
                    <div onClick={e => toggleFavorite(pair.symbol, e)} className="flex items-center justify-center cursor-pointer">
                      <svg
                        width="13"
                        height="13"
                        viewBox="0 0 24 24"
                        fill={favorites.has(pair.symbol) ? '#f59e0b' : 'none'}
                        stroke={favorites.has(pair.symbol) ? '#f59e0b' : '#4a4a4a'}
                        strokeWidth="2"
                      >
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                      </svg>
                    </div>

                    {/* Symbol + Logo + Leverage */}
                    <div className="flex items-center gap-2 min-w-0">
                      <CryptoLogo symbol={pair.base} size={22} />
                      <div className="flex flex-col items-start min-w-0">
                        <span className="text-sm font-semibold text-[#F1F5F9] leading-tight truncate">
                          {pair.base}USDT
                        </span>
                        {pair.leverage && (
                          <span className="text-[10px] px-1 py-px bg-[#1e3a2a] text-[#22C55E] font-medium leading-tight mt-0.5">
                            {pair.leverage}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Last Price */}
                    <div className="text-right text-sm font-mono tabular-nums text-[#F1F5F9]">
                      {pair.price}
                    </div>

                    {/* 24h Change */}
                    <div className={`text-right text-sm font-mono tabular-nums ${pair.positive ? 'text-[#22C55E]' : 'text-[#EF4444]'}`}>
                      {pair.change}
                    </div>

                    {/* Funding Rate */}
                    <div className={`text-right text-xs font-mono tabular-nums ${
                      pair.fundingRate?.startsWith('-') ? 'text-[#EF4444]' : 'text-[#22C55E]'
                    }`}>
                      {pair.fundingRate ?? '0.0000%'}
                    </div>

                    {/* Volume */}
                    <div className="text-right text-xs font-mono tabular-nums text-[#94A3B8]">
                      {pair.volume}
                    </div>

                    {/* Open Interest */}
                    <div className="text-right text-xs font-mono tabular-nums text-[#94A3B8]">
                      {pair.openInterest ?? '-'}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Live Price Stats */}
      <div className="flex items-center gap-1">
        <span className={`text-2xl font-bold font-mono tabular-nums ${displayPositive ? 'text-[#22C55E]' : 'text-[#EF4444]'}`}>
          ${displayPrice}
        </span>
        <span className={`text-sm font-mono px-2 py-0.5 ${displayPositive ? 'bg-[rgba(34,197,94,0.12)] text-[#22C55E]' : 'bg-[rgba(239,68,68,0.12)] text-[#EF4444]'}`}>
          {displayChange}
        </span>
        {(liveStats || !loading) && (
          <span className="flex items-center gap-1 ml-1">
            <span className="w-1.5 h-1.5 bg-[#22C55E] animate-pulse" />
          </span>
        )}
      </div>

      <div className="hidden lg:flex items-center gap-4 text-xs text-[#94A3B8]">
        <div>
          <span className="block text-[10px] uppercase tracking-wider mb-0.5">24h High</span>
          <span className="font-mono text-[#F1F5F9]">${selectedPair.high}</span>
        </div>
        <div>
          <span className="block text-[10px] uppercase tracking-wider mb-0.5">24h Low</span>
          <span className="font-mono text-[#F1F5F9]">${selectedPair.low}</span>
        </div>
        <div>
          <span className="block text-[10px] uppercase tracking-wider mb-0.5">24h Volume</span>
          <span className="font-mono text-[#F1F5F9]">${displayVolume}</span>
        </div>
      </div>
    </div>
  );
}

// Export ETH_FALLBACK as the default pair for TradingDashboardInteractive
export { ETH_FALLBACK as DEFAULT_PAIR };
export type { TradingPair as TradingPairType };